<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :minimized-width="minimizedWidth"
    :minimized="isMinimized"
    :animated="true"
  >
    <div class="sidebar-menu-header" :class="{ 'sidebar-menu-header--minimized': isMinimized }">
      <span v-if="!isMinimized" class="sidebar-menu-header__title">Main Menu</span>
      <button
        type="button"
        class="sidebar-menu-header__toggle"
        :aria-label="isMinimized ? 'Expand menu' : 'Collapse menu'"
        @click="toggleMinimized"
      >
        <VaIconMenuCollapsed
          class="sidebar-menu-header__icon"
          :class="{ 'x-flip': !isMinimized }"
          color="rgba(231, 238, 252, 0.9)"
        />
      </button>
    </div>

    <!-- Single-link client menu (no submenus) -->
    <template v-if="isMinimized">
      <div class="collapsed-sidebar">
        <VaSidebarItem
          v-for="(route, index) in clientRoutes"
          :key="index"
          :to="{ name: route.name }"
          :active="isActive(route)"
          role="button"
          hover-opacity="0.1"
          class="collapsed-item"
        >
          <VaSidebarItemContent class="py-3 px-2 flex justify-center">
            <VaIcon v-if="route.meta?.icon" :name="route.meta.icon" size="24px" :color="iconColor(route)" />
          </VaSidebarItemContent>
        </VaSidebarItem>
      </div>
    </template>

    <template v-else>
      <div class="client-sidebar-list">
        <VaSidebarItem
          v-for="(route, index) in clientRoutes"
          :key="index"
          :to="{ name: route.name }"
          :active="isActive(route)"
          role="button"
          hover-opacity="0.1"
        >
          <VaSidebarItemContent class="py-3 pr-2 pl-4">
            <VaIcon v-if="route.meta?.icon" :name="route.meta.icon" size="20px" :color="iconColor(route)" />
            <VaSidebarItemTitle class="leading-5 font-semibold">
              {{ route.displayName }}
            </VaSidebarItemTitle>
          </VaSidebarItemContent>
        </VaSidebarItem>
      </div>
    </template>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useColors } from 'vuestic-ui'
import ClientPortalNavigationRoutes, { type INavigationRoute } from './ClientPortalNavigationRoutes'
import VaIconMenuCollapsed from '@/components/icons/VaIconMenuCollapsed.vue'
export default defineComponent({
  name: 'ClientPortalSidebar',
  components: { VaIconMenuCollapsed },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible', 'update:minimized'],

  setup(props, { emit }) {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActive = (r: INavigationRoute) => route.name === r.name

    const isMinimized = computed(() => props.minimized)

    function toggleMinimized() {
      emit('update:minimized', !props.minimized)
    }

    const minimizedWidth = computed(() => '64px')
    const sidebarWidth = computed(() => {
      if (props.mobile) return '100vw'
      if (isMinimized.value) return minimizedWidth.value
      return '280px'
    })

    const iconColor = (r: INavigationRoute) => (isActive(r) ? 'primary' : 'secondary')

    const clientRoutes = computed(() => ClientPortalNavigationRoutes.routes)

    return {
      writableVisible,
      sidebarWidth,
      minimizedWidth,
      isMinimized,
      clientRoutes,
      isActive,
      iconColor,
      toggleMinimized,
    }
  },
})
</script>

<style scoped>
.va-sidebar-item-title {
  text-transform: none;
}

.va-sidebar {
  /* Match screenshot: dark/navy gradient sidebar */
  background: radial-gradient(1200px 900px at 0% 0%, #1b2a55 0%, #0b1220 55%, #070b14 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.client-sidebar-list :deep(.va-sidebar-item) {
  border-radius: 10px;
  margin: 0.15rem 0.5rem;
}

.client-sidebar-list :deep(.va-sidebar-item--active) {
  background: rgba(52, 114, 240, 0.22);
  border: 1px solid rgba(52, 114, 240, 0.35);
}

.client-sidebar-list :deep(.va-sidebar-item-title) {
  color: rgba(231, 238, 252, 0.82);
  font-weight: 700;
}

.client-sidebar-list :deep(.va-sidebar-item--active .va-sidebar-item-title) {
  color: #ffffff;
}

.collapsed-sidebar :deep(.va-sidebar-item) {
  border-radius: 12px;
  margin: 0.25rem 0.5rem;
}

.client-sidebar-list {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}

.collapsed-sidebar {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
}

.collapsed-item {
  margin: 0.25rem 0;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.collapsed-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.collapsed-item .va-sidebar-item-content {
  padding: 0.75rem 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.sidebar-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1rem 0.65rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.sidebar-menu-header--minimized {
  justify-content: center;
  padding-inline: 0.5rem;
}

.sidebar-menu-header__title {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(231, 238, 252, 0.72);
}

.sidebar-menu-header__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.sidebar-menu-header__toggle:hover {
  background: rgba(255, 255, 255, 0.12);
}

.sidebar-menu-header__icon {
  width: 20px;
  height: 20px;
}

.x-flip {
  transform: scaleX(-1);
}
</style>
